export const joinUrl = (baseUrl, path = "") => {
  const root = (baseUrl || "").trim().replace(/\/+$/, "");
  const suffix = (path || "")
    .toString()
    .trim()
    .replace(/^\/+|\/+$/g, "");
  if (!root) {
    return suffix ? `/${suffix}` : "/";
  }
  return suffix ? `${root}/${suffix}` : root;
};

export const resolveMediaUrl = (url) => {
  if (!url) return "";
  const value = String(url).trim();
  if (/^(https?:)?\/\//i.test(value) || value.startsWith("data:")) {
    return value;
  }

  const apiBase = process.env.REACT_APP_BASE_URL || "";
  if (value.startsWith("/api/") && /^https?:\/\//i.test(apiBase)) {
    return `${apiBase.replace(/\/api\/?$/, "")}${value}`;
  }

  return value.startsWith("/") ? value : `/${value}`;
};

export const toAbsoluteMediaUrl = (url, canonicalUrl) => {
  if (!url) return "";
  const value = String(url).trim();
  const marker = "/api/Tenant-Images/";
  const idx = value.toLowerCase().indexOf(marker.toLowerCase());
  if (idx >= 0) {
    const fileName = value.slice(idx + marker.length).split(/[?#]/)[0].replace(/^\/+/, "");
    return joinUrl(canonicalUrl, `tenant-images/${fileName}`);
  }
  if (/^(https?:)?\/\//i.test(value) || value.startsWith("data:")) {
    return value;
  }
  return joinUrl(canonicalUrl, value);
};

export const blogDetailPath = (blog) => {
  const key = blog?.slug || blog?.id;
  if (!key) return "/blog/index";
  return `/blog/detail/${encodeURIComponent(key)}`;
};

export const truncateText = (text, max = 160) => {
  if (!text) return "";
  const clean = String(text).replace(/\s+/g, " ").trim();
  if (clean.length <= max) return clean;
  return `${clean.slice(0, max - 1).replace(/[.\s،]+$/, "")}…`;
};

export const isPublicSeoPath = (pathname = "") => {
  const path = pathname.replace(/\/+$/, "") || "/";
  if (path === "/") return true;
  if (path === "/about" || path === "/support") return true;
  if (path === "/blog/index") return true;
  if (/^\/blog\/detail\/[^/]+$/i.test(path)) return true;
  if (path === "/reservation/reserve") return true;
  if (path === "/counseling" || path === "/counseling/index") return true;
  return false;
};

export const doctorDisplayName = (content) => {
  const name = content?.doctorName || content?.siteName || "پزشک";
  return name.startsWith("دکتر") ? name : `دکتر ${name}`;
};

export const resolveAboutPageTitle = (content) => {
  const rawName = (content?.doctorName || "").trim();
  const siteName = (content?.siteName || "سایت پزشک").trim();
  const doctorName = doctorDisplayName(content);
  const stored = (content?.aboutPageTitle || "").trim();
  if (stored && rawName && stored.includes(rawName)) {
    return stored;
  }
  return `درباره ${doctorName} | ${siteName}`;
};

export const buildPhysicianJsonLd = (content, pageUrl) => {
  const origin = joinUrl(content?.canonicalUrl, "");
  const name = doctorDisplayName(content);
  return {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Physician",
        "@id": `${origin}#physician`,
        name,
        url: origin,
        image: toAbsoluteMediaUrl(
          content?.doctorAvatarUrl ||
            content?.doctorImageUrl ||
            content?.ogImage,
          origin
        ),
        jobTitle: content?.doctorTitle || content?.doctorSpecialty,
        medicalSpecialty: content?.doctorSpecialty || undefined,
        telephone:
          content?.phoneNumber ||
          content?.clinicPhone ||
          content?.workplacePhone,
        email: content?.email || undefined,
        address: content?.address
          ? {
              "@type": "PostalAddress",
              addressCountry: "IR",
              streetAddress: content.address,
            }
          : undefined,
      },
      {
        "@type": "MedicalClinic",
        "@id": `${origin}#clinic`,
        name: content?.siteName,
        url: origin,
        image: toAbsoluteMediaUrl(
          content?.ogImage || content?.doctorImageUrl,
          origin
        ),
        telephone: content?.clinicPhone || content?.phoneNumber,
      },
      {
        "@type": "WebSite",
        "@id": `${origin}#website`,
        url: origin,
        name: content?.siteName,
        inLanguage: "fa-IR",
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            name: "صفحه اصلی",
            item: origin,
          },
          pageUrl && pageUrl !== origin
            ? {
                "@type": "ListItem",
                position: 2,
                name: content?.siteName,
                item: pageUrl,
              }
            : null,
        ].filter(Boolean),
      },
    ],
  };
};

export const buildBlogPostingJsonLd = (blog, content, pageUrl) => {
  const origin = joinUrl(content?.canonicalUrl, "");
  return {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: blog?.title,
    description: truncateText(blog?.abstract || "", 160),
    image: toAbsoluteMediaUrl(
      blog?.imageUrl || content?.ogImage || content?.doctorImageUrl,
      origin
    ),
    datePublished: blog?.date,
    inLanguage: "fa-IR",
    mainEntityOfPage: pageUrl,
    author: {
      "@type": "Person",
      name: blog?.authorName || doctorDisplayName(content),
    },
    publisher: {
      "@type": "Organization",
      name: content?.siteName,
      logo: {
        "@type": "ImageObject",
        url: toAbsoluteMediaUrl(content?.logoUrl || content?.ogImage, origin),
      },
    },
    isPartOf: {
      "@type": "Blog",
      name: `مقالات ${content?.siteName || ""}`.trim(),
      url: joinUrl(origin, "blog/index"),
    },
  };
};
